// =================================================================================
// File: src/pages/visitor/PublicStudioPage.jsx
// Description: Public profile page for a specific studio with workshops and spaces
// =================================================================================

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, MapPin, Globe, Calendar, ExternalLink, 
  Users, Clock, Package 
} from "lucide-react";
import { studiosApi } from "../../lib/mudoNetwork";
import { formatPrice, formatDateRange, getDuration } from "../../utils/formatters";
import { apiFetch } from "../../lib/apiFetch";

/**
 * PublicStudioPage - View a studio's public profile
 * 
 * Shows:
 * - Studio info (name, location, bio, hero image)
 * - Upcoming workshops with booking
 * - Available studio spaces
 * - Website and contact links
 */
export default function PublicStudioPage() {
  const { studioSlug } = useParams();
  const navigate = useNavigate();

  const [studio, setStudio] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch studio data
  useEffect(() => {
    async function fetchStudioData() {
      if (!studioSlug) return;

      try {
        setLoading(true);
        
        // Fetch studio by slug
        const { data: studioData, error: studioError } = await studiosApi.getStudioBySlug(studioSlug);
        
        if (studioError) throw studioError;
        if (!studioData) {
          setError("Studio not found");
          setLoading(false);
          return;
        }

        setStudio(studioData);

        // Fetch studio's workshops
        const workshopsRes = await apiFetch(
          `/api/public/studio/${encodeURIComponent(String(studioData.id))}/workshops?limit=20&from=${encodeURIComponent(
            new Date().toISOString()
          )}`
        );
        const workshopsJson = await workshopsRes.json();
        const workshopsRows = Array.isArray(workshopsJson) ? workshopsJson : workshopsJson?.data;
        setWorkshops(Array.isArray(workshopsRows) ? workshopsRows : []);

        // Fetch studio's spaces
        const spacesRes = await apiFetch(
          `/api/public/studio/${encodeURIComponent(String(studioData.id))}/spaces`
        );
        const spacesJson = await spacesRes.json();
        const spacesRows = Array.isArray(spacesJson) ? spacesJson : spacesJson?.data;
        setSpaces(Array.isArray(spacesRows) ? spacesRows : []);

        setError(null);
      } catch (err) {
        console.error("Error fetching studio data:", err);
        setError("Failed to load studio information");
      } finally {
        setLoading(false);
      }
    }

    fetchStudioData();
  }, [studioSlug]);

  // Handle workshop booking (placeholder)
  const handleBookWorkshop = async (workshop) => {
    try {
      const resp = await apiFetch("/api/workshops/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workshopId: workshop.id, seats: 1 }),
      });
      const json = await resp.json();
      if (!resp.ok || !json?.ok || !json?.url) {
        throw new Error(json?.error || "Could not start checkout");
      }
      window.location.href = json.url;
    } catch (err) {
      alert(err?.message || "Booking failed");
    }
  };

  // Handle space booking (placeholder)
  const handleViewSpaceAvailability = (space) => {
    console.log("View space availability:", space.id);
    // TODO: Show available slots or navigate to booking calendar
    alert("Space booking coming soon! This will show available time slots.");
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/70">Loading studio...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !studio) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center max-w-md px-4">
          <MapPin className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">{error || "Studio not found"}</h2>
          <button
            onClick={() => navigate("/discover/map")}
            className="px-6 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 transition-colors font-semibold"
          >
            Back to Map
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Top Navigation */}
      <div className="bg-gray-950/50 backdrop-blur-md border-b border-white/10 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/discover/map")}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-white text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Map
          </button>

          <button
            onClick={() => {
              try {
                localStorage.setItem(
                  "mudo:switchToStudioIntentFrom",
                  window.location.pathname + window.location.search
                );
              } catch {}
              localStorage.setItem("mudo:userRole", "studio");
              navigate("/dashboard");
            }}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors"
          >
            Switch to Studio
          </button>
        </div>
      </div>

      {/* Studio Hero Section */}
      <div className="relative h-64 md:h-80 overflow-hidden bg-gradient-to-br from-amber-500/30 to-orange-500/30">
        {studio.hero_image_url ? (
          <img
            src={studio.hero_image_url}
            alt={studio.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-24 h-24 text-white/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
      </div>

      {/* Studio Info */}
      <div className="max-w-6xl mx-auto px-4 -mt-20 relative z-10">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 mb-8">
          {/* Studio Name & Location */}
          <div className="mb-6">
            <h1 className="text-4xl font-bold mb-3">{studio.name}</h1>
            {(studio.city || studio.country) && (
              <div className="flex items-center gap-2 text-white/70 text-lg">
                <MapPin className="w-5 h-5" />
                <span>{[studio.city, studio.country].filter(Boolean).join(", ")}</span>
              </div>
            )}
          </div>

          {/* Bio */}
          {studio.bio && (
            <div className="mb-6">
              <p className="text-white/80 text-lg leading-relaxed">{studio.bio}</p>
            </div>
          )}

          {/* Website Link */}
          {studio.website_url && (
            <div className="pt-6 border-t border-white/10">
              <a
                href={studio.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 transition-colors group"
              >
                <Globe className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-semibold">Visit Website</span>
                <ExternalLink className="w-4 h-4 ml-2 opacity-50 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          )}
        </div>

        {/* Workshops Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-blue-500" />
            Workshops
          </h2>

          {workshops.length === 0 ? (
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-12 text-center">
              <Calendar className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/60">No upcoming workshops scheduled</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workshops.map((workshop) => (
                <WorkshopCard
                  key={workshop.id}
                  workshop={workshop}
                  onBook={handleBookWorkshop}
                />
              ))}
            </div>
          )}
        </div>

        {/* Studio Spaces Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Package className="w-8 h-8 text-green-500" />
            Studio Spaces
          </h2>

          {spaces.length === 0 ? (
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-12 text-center">
              <Package className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/60">No studio spaces available for booking</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {spaces.map((space) => (
                <SpaceCard
                  key={space.id}
                  space={space}
                  onViewAvailability={handleViewSpaceAvailability}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * WorkshopCard Component
 */
function WorkshopCard({ workshop, onBook }) {
  const seatsAvailable = workshop.capacity - workshop.seats_taken;
  const isFull = seatsAvailable <= 0;

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 group">
      {/* Workshop Image */}
      <div className="relative h-40 bg-gradient-to-br from-blue-500/30 to-purple-500/30">
        {workshop.hero_image_url ? (
          <img
            src={workshop.hero_image_url}
            alt={workshop.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="w-12 h-12 text-white/30" />
          </div>
        )}

        {isFull && (
          <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-red-600 text-white text-xs font-semibold">
            Full
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold mb-2 line-clamp-2">{workshop.title}</h3>

        <div className="space-y-2 mb-4 text-sm text-white/70">
          <div className="flex items-start gap-2">
            <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">{formatDateRange(workshop.start_at, workshop.end_at)}</span>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{getDuration(workshop.start_at, workshop.end_at)} hours</span>
          </div>

          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 flex-shrink-0" />
            <span>
              {isFull ? "Fully booked" : `${seatsAvailable} spots left`}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <div className="text-xl font-bold text-blue-500">
            {formatPrice(workshop.price_cents, workshop.currency)}
          </div>

          <button
            onClick={() => onBook(workshop)}
            disabled={isFull}
            className={`px-5 py-2 rounded-lg font-semibold transition-all text-sm ${
              isFull
                ? "bg-white/5 text-white/30 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white hover:scale-105"
            }`}
          >
            {isFull ? "Full" : "Book"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * SpaceCard Component
 */
function SpaceCard({ space, onViewAvailability }) {
  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 group">
      {/* Space Image */}
      <div className="relative h-40 bg-gradient-to-br from-green-500/30 to-teal-500/30">
        {space.image_url ? (
          <img
            src={space.image_url}
            alt={space.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-white/30" />
          </div>
        )}

        {/* Type Badge */}
        <div className="absolute top-2 left-2 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-semibold capitalize">
          {space.type}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold mb-2">{space.name}</h3>

        {space.description && (
          <p className="text-sm text-white/60 mb-4 line-clamp-2">{space.description}</p>
        )}

        <button
          onClick={() => onViewAvailability(space)}
          className="w-full px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-all hover:scale-105 text-sm"
        >
          View Availability
        </button>
      </div>
    </div>
  );
}
